import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Send, Instagram, Facebook } from "lucide-react";
import { api } from "../lib/api";
import { useSettings } from "../lib/SettingsContext";
import { toast } from "sonner";

const TikTokIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.16a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.59z"/>
  </svg>
);

export default function Footer() {
  const { settings } = useSettings();
  const [email, setEmail] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/newsletter", { email });
      toast.success("Mulțumim pentru abonare");
      setEmail("");
    } catch { toast.error("Eroare la abonare"); }
  };

  const brandName = settings.brand_name || "Johny's Antique Shop";
  const brandLogo = settings.brand_logo || "";
  const description = settings.footer_description || "Casă de antichități rafinată. Selectăm cele mai rare obiecte de patrimoniu pentru colecționarii cei mai exigenți.";
  const tiktok = settings.social_tiktok || "";
  const instagram = settings.social_instagram || "";
  const facebook = settings.social_facebook || "";
  const newsletterTitle = settings.newsletter_title || "Jurnalul Nostru";
  const newsletterSubtitle = settings.newsletter_subtitle || "Înscrie-te pentru a primi acces în avanpremieră la noile colecții.";

  return (
    <footer className="border-t border-[#D4AF37]/10 bg-[#050505] mt-24" data-testid="footer">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 grid grid-cols-1 md:grid-cols-4 gap-12">
        <div>
          {brandLogo ? (
            <img src={brandLogo} alt={brandName} className="h-16 w-auto object-contain mb-3" />
          ) : null}
          <div className="font-serif text-3xl text-[#D4AF37] mb-2">{brandName}</div>
          <p className="text-[#A39E8A] text-sm leading-relaxed font-light">{description}</p>
          <div className="flex gap-4 mt-6">
            {facebook && (
              <a href={facebook} target="_blank" rel="noreferrer" className="w-10 h-10 border border-[#D4AF37]/30 hover:border-[#D4AF37] flex items-center justify-center transition text-[#D4AF37]" data-testid="facebook-link" aria-label="Facebook">
                <Facebook size={16} strokeWidth={1.6} />
              </a>
            )}
            {instagram && (
              <a href={instagram} target="_blank" rel="noreferrer" className="w-10 h-10 border border-[#D4AF37]/30 hover:border-[#D4AF37] flex items-center justify-center transition text-[#D4AF37]" data-testid="instagram-link" aria-label="Instagram">
                <Instagram size={16} strokeWidth={1.4} />
              </a>
            )}
            {tiktok && (
              <a href={tiktok} target="_blank" rel="noreferrer" className="w-10 h-10 border border-[#D4AF37]/30 hover:border-[#D4AF37] flex items-center justify-center transition text-[#D4AF37]" data-testid="tiktok-link" aria-label="TikTok">
                <TikTokIcon size={16} />
              </a>
            )}
          </div>
        </div>
        <div>
          <p className="label-luxe mb-5">Magazin</p>
          <ul className="space-y-3 text-sm text-[#A39E8A]">
            <li><Link to="/shop" className="hover:text-[#D4AF37] transition">Toate Categoriile</Link></li>
            <li><Link to="/shop?category=Mobilier+Antic" className="hover:text-[#D4AF37] transition">Mobilier Antic</Link></li>
            <li><Link to="/shop?category=Ceasuri+Vintage" className="hover:text-[#D4AF37] transition">Ceasuri Vintage</Link></li>
            <li><Link to="/shop?category=Picturi+Rare" className="hover:text-[#D4AF37] transition">Picturi Rare</Link></li>
            <li><Link to="/shop?category=Bijuterii+de+Lux" className="hover:text-[#D4AF37] transition">Bijuterii de Lux</Link></li>
          </ul>
        </div>
        <div>
          <p className="label-luxe mb-5">Companie</p>
          <ul className="space-y-3 text-sm text-[#A39E8A]">
            <li><Link to="/about" className="hover:text-[#D4AF37]">Despre Noi</Link></li>
            <li><Link to="/contact" className="hover:text-[#D4AF37]">Contact</Link></li>
            <li><Link to="/faq" className="hover:text-[#D4AF37]">Întrebări Frecvente</Link></li>
            <li><Link to="/legal" className="hover:text-[#D4AF37]">Termeni & Condiții</Link></li>
            <li><Link to="/legal#privacy" className="hover:text-[#D4AF37]">Politica de Confidențialitate</Link></li>
          </ul>
        </div>
        <div>
          <p className="label-luxe mb-5">{newsletterTitle}</p>
          <p className="text-sm text-[#A39E8A] mb-5">{newsletterSubtitle}</p>
          <form onSubmit={submit} className="flex border-b border-[#D4AF37]/30 focus-within:border-[#D4AF37]" data-testid="newsletter-form">
            <input
              type="email" required value={email} onChange={e => setEmail(e.target.value)}
              placeholder="Adresa de email"
              className="flex-1 bg-transparent py-3 text-sm text-[#F8F4E6] outline-none placeholder:text-[#A39E8A]/60 placeholder:uppercase placeholder:tracking-[0.2em] placeholder:text-[10px]"
              data-testid="newsletter-email"
            />
            <button type="submit" className="text-[#D4AF37]" data-testid="newsletter-submit">
              <Send size={16} strokeWidth={1.4} />
            </button>
          </form>
        </div>
      </div>
      <div className="border-t border-[#D4AF37]/10 py-8 px-6 lg:px-12 max-w-[1400px] mx-auto" data-testid="footer-bottom">
        <div className="flex flex-col md:flex-row gap-6 justify-between items-center mb-6">
          <span className="text-[11px] uppercase tracking-[0.2em] text-[#A39E8A]/70">© {new Date().getFullYear()} {brandName}. Toate drepturile rezervate.</span>
          <span className="text-[11px] uppercase tracking-[0.2em] text-[#A39E8A]/70">Fabricat cu eleganță în România</span>
        </div>
        <div className="flex flex-wrap justify-center md:justify-end items-center gap-6 pt-6 border-t border-[#D4AF37]/10" data-testid="authority-logos">
          <a href="https://anpc.ro/ce-este-sal/" target="_blank" rel="noreferrer" className="opacity-70 hover:opacity-100 transition" title="ANPC - Soluționarea Alternativă a Litigiilor" data-testid="anpc-link">
            <img src="https://customer-assets.emergentagent.com/job_rare-collectibles-5/artifacts/qjo5qcan_image.png" alt="ANPC - Autoritatea Națională pentru Protecția Consumatorilor" className="h-14 w-auto object-contain" />
          </a>
          <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noreferrer" className="opacity-70 hover:opacity-100 transition" title="Soluționarea Online a Litigiilor (SOL)" data-testid="anspdcp-link">
            <img src="https://customer-assets.emergentagent.com/job_rare-collectibles-5/artifacts/oz0s8jia_image.png" alt="ANSPDCP - Autoritatea Națională de Supraveghere a Prelucrării Datelor cu Caracter Personal" className="h-14 w-auto object-contain" />
          </a>
        </div>
      </div>
    </footer>
  );
}
